/**
 * FoliageTranslucency — cheap two-sided translucent lighting for leaves,
 * paper, curtains: the classic back-transmission term
 *
 *   TT = pow(saturate(V · −L), backPower) · backIntensity
 *
 * added as emissive tinted by the translucency colour, plus a wrap factor
 * that lifts the terminator (half-Lambert family). The light direction is a
 * UNIFORM (the sun/key light) — foliage TT tracks the dominant light, not
 * the whole light list, which is the standard real-time approximation.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec3, positionViewDirection, normalView,
  pow, saturate, normalize,
} from 'three/tsl';

export interface FoliageTranslucencyConfig {
  /** Transmission tint (default leafy 0.35, 0.55, 0.12). */
  color?: [number, number, number];
  /** View-alignment exponent — higher = tighter sun-behind-leaf glow (default 4). */
  backPower?: number;
  /** Transmission strength (default 0.6). */
  backIntensity?: number;
  /**
   * Incident light intensity (default 1) and colour (default white). The
   * transmission is LIGHT passing through the leaf: without these the term was
   * a constant glow that ignored the sun going down (audit 16.2, 2026-09-15).
   */
  lightIntensity?: number;
  lightColor?: [number, number, number];
  /** Light direction in VIEW space (default sun-ish 0.3,0.5,0.8). */
  lightDirView?: [number, number, number];
  /** Terminator wrap 0..1 added to N·L shaping (default 0.4). */
  wrap?: number;
}

export interface FoliageTranslucencyHandle {
  color: { value: THREE.Vector3 };
  backPower: { value: number };
  backIntensity: { value: number };
  lightIntensity: { value: number };
  lightColor: { value: THREE.Vector3 };
  lightDirView: { value: THREE.Vector3 };
  wrap: { value: number };
}

/** CPU mirror of the back-transmission term (exported for tests). */
export function backTransmissionCpu(
  vDotNegL: number, backPower: number, backIntensity: number,
): number {
  return Math.pow(Math.max(vDotNegL, 0), backPower) * backIntensity;
}

/** CPU mirror of wrap-shaped N·L (exported for tests). */
export function wrapDiffuseCpu(nDotL: number, wrap: number): number {
  const w = Math.min(Math.max(wrap, 0), 1);
  return Math.max((nDotL + w) / (1 + w), 0);
}

export function applyFoliageTranslucency(
  material: THREE.Material & { emissiveNode?: unknown; side?: THREE.Side },
  config?: FoliageTranslucencyConfig,
): FoliageTranslucencyHandle {
  const c = config?.color ?? [0.35, 0.55, 0.12];
  const ld = config?.lightDirView ?? [0.3, 0.5, 0.8];
  const uColor = uniform(vec3(c[0], c[1], c[2]));
  const uBackPower = uniform(config?.backPower ?? 4);
  const uBackIntensity = uniform(config?.backIntensity ?? 0.6);
  const uLightDir = uniform(vec3(ld[0], ld[1], ld[2]));
  const uWrap = uniform(config?.wrap ?? 0.4);
  const lc = config?.lightColor ?? [1, 1, 1];
  const uLightIntensity = uniform(config?.lightIntensity ?? 1);
  const uLightColor = uniform(vec3(lc[0], lc[1], lc[2]));

  const prevEmissive = material.emissiveNode;
  material.emissiveNode = Fn(() => {
    const l = normalize(uLightDir);
    // Sun shining THROUGH the leaf toward the eye: V aligned with −L.
    const vDotNegL = saturate(positionViewDirection.dot(l.negate()));
    const tt = pow(vDotNegL, uBackPower).mul(uBackIntensity);
    // Wrap lift: brightens the terminator-side transmission so backlit
    // edges glow before the exact anti-light alignment.
    const nDotL = normalView.dot(l);
    const wrapped = saturate(nDotL.add(uWrap).div(float(1).add(uWrap)));
    const base = (prevEmissive ?? vec3(0, 0, 0)) as never;
    // Scaled by the INCIDENT light: no sun, no transmission.
    return vec3(base).add(uColor.mul(uLightColor).mul(uLightIntensity).mul(tt.add(wrapped.mul(uBackIntensity).mul(0.15))));
  })();
  material.needsUpdate = true;

  return {
    color: uColor as unknown as { value: THREE.Vector3 },
    backPower: uBackPower as unknown as { value: number },
    backIntensity: uBackIntensity as unknown as { value: number },
    lightDirView: uLightDir as unknown as { value: THREE.Vector3 },
    wrap: uWrap as unknown as { value: number },
    lightIntensity: uLightIntensity as unknown as { value: number },
    lightColor: uLightColor as unknown as { value: THREE.Vector3 },
  };
}
